<!-- 
Risk factor table for the levy calculation.

The risk factor table shows the different ranges for the risk factor and
highlights the range that the provider falls into.
-->
<template>
  <v-card flat class="ml-5">
    <v-card-text class="pa-0">
      <v-data-table 
        :headers="headers" 
        :items="factors" 
        :total-items="factors.length" 
        :loading="state.loading" 
        hide-actions 
        class="levy-calculation-risk-factor-table"
      >
        <template slot="items" slot-scope="props">
          <tr :class="getRowClass(props.item)">
            <td :class="getCellClass(props.item)" class="no-pad-right">
              {{ getBoundDescription(props.item) }}
            </td>
            <td :class="getCellClass(props.item)" class="no-pad-left">
              {{ props.item.score | numericInput2 }}
            </td>
          </tr>
        </template>
      </v-data-table>
    </v-card-text>
  </v-card>
</template>

<script>
import * as action from "@/store/levy-collection-risk-factors/action-types";
import * as estimatorAction from "@/store/levy-estimator/action-types";
import filterMixins from "@/mixins/filter.mixins";
import lookupMixins from "@/mixins/lookup.mixins";
import * as formatting from "@/helpers/formatting.helper";

export default {
  mixins: [filterMixins, lookupMixins],
  props: {
    type: {
      type: String,
      required: true
    },
    title: {
      type: String,
      required: true
    },
    terminology: {
      type: String,
      default: "Value"
    },
    hideTitle: {
      type: Boolean,
      default: false
    },
    tableTitle: {
      type: String,
      default: null
    },
    levyCollectionId: {
      validator: prop => typeof prop === "number" || prop === null,
      default: null
    },
    estimatorKey: {
      validator: prop => typeof prop === "string" || prop === null,
      default: null
    },
    isCurrency: {
      type: Boolean,
      default: false
    },
    isPercentage: {
      type: Boolean,
      default: false
    },
    selectFirstMatchingScore: {
      type: Boolean,
      default: true
    },
    selectedScore: {
      validator: prop => typeof prop === "number" || prop === null,
      default: null
    }
  },
  data() {
    return {
      headers: [
        { text: this.tableTitle ? this.tableTitle : this.title, align: "left", sortable: false, value: "range" },
        { text: this.terminology, align: "center", sortable: false, value: "score" }
      ]
    };
  },
  computed: {
    isEstimator() {
      return this.estimatorKey != null;
    },
    state() {
      if (this.isEstimator) {
        return this.$store.state.levyEstimator.estimator;
      } else {
        return this.$store.state.levyCollectionRiskFactors.listing;
      }
    },
    loaded() {
      return this.state.loaded;
    },
    riskFactorType() {
      return this.getLookup(this.type, this.lookups.riskFactorTypes);
    },
    factors() {
      const list = this.isEstimator ? this.state.data.riskFactors : this.state.data;
      return list.filter(r => r.type == this.type);
    }
  },
  watch: {
    levyCollectionId() {
      this.reload();
    },
    estimatorKey() {
      this.reload();
    },
    loaded() {
      this.reload();
    }
  },
  mounted() {
    this.reload();
  },
  methods: {
    reload() {
      if (this.isEstimator) {
        this.$store.dispatch(estimatorAction.VIEW_REFRESH, this.estimatorKey);
      } else if (this.levyCollectionId) {
        const isDifferentLevyCollection = this.state.levyCollectionId != this.levyCollectionId;
        if ((!this.state.loaded && !this.state.loading) || isDifferentLevyCollection) {
          this.$store.dispatch(action.VIEW, this.levyCollectionId);
        }
      }
    },
    getBoundDescription(riskFactor) {
      const description = formatting.getRiskFactorLowerBoundDescription(riskFactor, this.factors, this.riskFactorType);
      return riskFactor.customLabel ? riskFactor.customLabel : description;
    },
    getRowClass(riskFactor) {
      return this.isMatch(riskFactor) ? "highlight" : "noselect";
    },
    getCellClass(riskFactor) {
      return this.isMatch(riskFactor) ? "highlight" : "";
    },
    isMatch(riskFactor) {
      if (riskFactor.score != this.selectedScore) {
        return false;
      }
      // some risk factor tables have the same score for different ranges
      const matches = this.factors.filter(f => f.score == riskFactor.score);
      const isFirst = matches.indexOf(riskFactor) == 0;
      return (isFirst && this.selectFirstMatchingScore) || (!isFirst && !this.selectFirstMatchingScore);
    }
  }
};
</script>

<style lang="scss">
div.levy-calculation-risk-factor-table {
  table {
    border: 1px solid #f0f0f0;
  }
  table.v-table {
    th:nth-child(1) {
      padding-right: 2px !important;
    }
    th:nth-child(2) {
      padding-left: 2px !important;
    }
    tbody {
      tr.highlight,
      tr.highlight:hover {
        background-color: #f8f8f8 !important;
      }
      td {
        .highlight {
          font-weight: bold;
        }
        .no-pad-right {
          padding-right: 0 !important;
        }
        .no-pad-left {
          padding-left: 0 !important;
        }
      }
    }
  }
}
</style>